import {
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { useCallback, useMemo } from 'react';

import type {
  AppointmentPreviewField,
  AppointmentPreviewFieldKey,
} from '@pages/Calendar/data';

import type { DragEndEvent } from '@dnd-kit/core';

interface UseAppointmentPreviewSettingsParams {
  fields: AppointmentPreviewField[];
  onFieldsChange(fields: AppointmentPreviewField[]): void;
}

export const useAppointmentPreviewSettings = ({
  fields,
  onFieldsChange,
}: UseAppointmentPreviewSettingsParams) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const sortableIds = useMemo(() => fields.map(({ key }) => key), [fields]);

  // The last visible row cannot be switched off: an empty preview would hide
  // the appointment content entirely.
  const visibleFieldsCount = useMemo(
    () => fields.filter(({ isVisible }) => isVisible).length,
    [fields]
  );

  const moveField = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (
        fromIndex < 0 ||
        toIndex < 0 ||
        toIndex >= fields.length ||
        fromIndex === toIndex
      ) {
        return;
      }

      onFieldsChange(arrayMove(fields, fromIndex, toIndex));
    },
    [fields, onFieldsChange]
  );

  const dragEndHandler = useCallback(
    ({ active, over }: DragEndEvent) => {
      if (!over) return;

      moveField(
        fields.findIndex(({ key }) => key === active.id),
        fields.findIndex(({ key }) => key === over.id)
      );
    },
    [fields, moveField]
  );

  const moveFieldHandler = useCallback(
    (key: AppointmentPreviewFieldKey, offset: -1 | 1) => {
      const fromIndex = fields.findIndex((field) => field.key === key);

      moveField(fromIndex, fromIndex + offset);
    },
    [fields, moveField]
  );

  const toggleFieldHandler = useCallback(
    (key: AppointmentPreviewFieldKey, isVisible: boolean) => {
      onFieldsChange(
        fields.map((field) =>
          field.key === key ? { ...field, isVisible } : field
        )
      );
    },
    [fields, onFieldsChange]
  );

  return {
    sensors,
    sortableIds,
    visibleFieldsCount,
    dragEndHandler,
    moveFieldHandler,
    toggleFieldHandler,
  };
};
